import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Success component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconSuccess extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.60449 12C2.60449 6.81127 6.81078 2.60498 11.9995 2.60498C17.1882 2.60498 21.3945 6.81127 21.3945 12C21.3945 17.1887 17.1882 21.395 11.9995 21.395C6.81078 21.395 2.60449 17.1887 2.60449 12ZM11.9995 1.10498C5.98235 1.10498 1.10449 5.98284 1.10449 12C1.10449 18.0171 5.98235 22.895 11.9995 22.895C18.0166 22.895 22.8945 18.0171 22.8945 12C22.8945 5.98284 18.0166 1.10498 11.9995 1.10498ZM17.3298 7.6958C17.5386 7.33806 17.4178 6.87879 17.0601 6.67C16.7023 6.46121 16.2431 6.58196 16.0343 6.9397L11.0237 15.5248C11.0215 15.5286 11.0185 15.5319 11.0149 15.5344C11.0114 15.5369 11.0073 15.5385 11.003 15.5394L10.9935 15.5413L7.78593 12.9752C7.46249 12.7164 6.99052 12.7689 6.73176 13.0923C6.473 13.4158 6.52544 13.8877 6.84889 14.1465L10.045 16.7034C10.2159 16.8429 10.4155 16.9431 10.6296 16.9966C10.8441 17.0502 11.0678 17.0557 11.2846 17.0127C11.4988 16.9718 11.7018 16.8856 11.88 16.7598C12.0593 16.6332 12.2097 16.4688 12.3202 16.2791L17.3298 7.6958Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-success') === undefined) {
  customElements.define('cat-icon-success', BlocksIconSuccess);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-success': BlocksIconSuccess;
  }
}
